import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const STORAGE_KEY = "mdt-privacy-ack";

const COPY = {
  de: {
    badge: "Datenschutz · Transparenz",
    title: "Diese Seite verwendet keine Cookies.",
    body: "Wir messen Reichweite anonym (keine IP, kein Fingerprinting, keine Cookies). Mehr Details findest du in der Datenschutzerklärung.",
    ok: "Verstanden",
    more: "Datenschutz lesen",
    close: "Schließen",
  },
  en: {
    badge: "Privacy · Transparency",
    title: "This site does not use cookies.",
    body: "We measure traffic anonymously (no IP, no fingerprinting, no cookies). See our privacy policy for details.",
    ok: "Got it",
    more: "Read privacy policy",
    close: "Close",
  },
  es: {
    badge: "Privacidad · Transparencia",
    title: "Este sitio no utiliza cookies.",
    body: "Medimos el tráfico de forma anónima (sin IP, sin fingerprinting, sin cookies). Más detalles en la política de privacidad.",
    ok: "Entendido",
    more: "Leer política de privacidad",
    close: "Cerrar",
  },
};

const PrivacyBanner = () => {
  const { locale } = useLanguage();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const t = COPY[locale] || COPY.de;

  // Never show the banner inside the admin area.
  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdminRoute) return;
    try {
      const ack = localStorage.getItem(STORAGE_KEY);
      if (!ack) {
        const id = setTimeout(() => setVisible(true), 1200);
        return () => clearTimeout(id);
      }
    } catch (_) {
      setVisible(true);
    }
  }, [isAdminRoute]);

  if (isAdminRoute) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch (_) {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          role="dialog"
          aria-live="polite"
          aria-label={t.title}
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-testid="privacy-banner"
          className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-5 sm:left-5 sm:max-w-md z-[60]"
        >
          <div className="rounded-3xl bg-neutral-950 text-white shadow-[0_24px_60px_rgba(0,0,0,0.35)] border border-white/10 backdrop-blur p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 rounded-2xl bg-emerald-500/15 text-emerald-300 grid place-items-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-medium">
                  {t.badge}
                </div>
                <h3 className="font-display text-base sm:text-lg font-medium mt-1.5 leading-snug">
                  {t.title}
                </h3>
                <p className="mt-2 text-sm text-neutral-300 leading-relaxed">
                  {t.body}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={dismiss}
                    data-testid="privacy-banner-ack"
                    className="inline-flex items-center rounded-full bg-white text-neutral-950 px-4 py-2 text-xs font-medium hover:bg-neutral-100 transition-colors"
                  >
                    {t.ok}
                  </button>
                  <Link
                    to="/datenschutz"
                    onClick={dismiss}
                    data-testid="privacy-banner-more"
                    className="inline-flex items-center rounded-full border border-white/20 px-4 py-2 text-xs font-medium hover:bg-white/5 transition-colors"
                  >
                    {t.more}
                  </Link>
                </div>
              </div>
              <button
                type="button"
                onClick={dismiss}
                aria-label={t.close}
                data-testid="privacy-banner-close"
                className="shrink-0 text-neutral-400 hover:text-white transition-colors p-1 -mt-1 -mr-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default PrivacyBanner;
